import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth as authApi, getStored, setStored, setActivePropertyId } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getStored()?.user || null);
  const [initializing, setInitializing] = useState(true);

  // On boot, if we have a token, confirm it's still valid (and refresh profile).
  useEffect(() => {
    let cancelled = false;
    const stored = getStored();
    if (!stored?.accessToken) {
      setInitializing(false);
      return;
    }
    authApi
      .me()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        setStored({ ...getStored(), user: me });
      })
      .catch(() => {
        if (cancelled) return;
        setStored(null);
        setUser(null);
      })
      .finally(() => {
        if (!cancelled) setInitializing(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const acceptAuthResult = useCallback((result) => {
    setStored(result);
    if (result.user?.propertyIds?.length) {
      setActivePropertyId(result.user.propertyIds[0]);
    }
    setUser(result.user);
    return result.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await authApi.login(email, password);
    return acceptAuthResult(result);
  }, [acceptAuthResult]);

  /** Passkey sign-in: WebAuthn assertion already verified by the API. */
  const loginWithAuthResult = useCallback((result) => acceptAuthResult(result), [acceptAuthResult]);

  const updateUser = useCallback((next) => {
    setUser(next);
    setStored({ ...getStored(), user: next });
  }, []);

  const logout = useCallback(async () => {
    const stored = getStored();
    if (stored?.refreshToken) await authApi.logout(stored.refreshToken);
    setStored(null);
    setActivePropertyId(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, isAuthenticated: !!user, initializing, login, loginWithAuthResult, logout, updateUser }),
    [user, initializing, login, loginWithAuthResult, logout, updateUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
